import React, { useState, useEffect, useCallback, useRef } from "react";
import HospitalCustomPaginaion from "../utils/HospitalPaginationUtil";
import { ReportServices } from "../services/ReportServices";

/** Page size for cc-doctor-case-count-report (must match prefetch in DoctorPerformanceReport). */
export const DOCTOR_STATS_PAGE_LIMIT = 10;

const CELL_CLASS =
  "font-lufga font-normal text-sm leading-[100%] tracking-[-0.03em] text-black";
const HEADER_CLASS = "font-lufga font-medium text-sm leading-[18px] text-black";

/** Normalize period report (handles processedStages vs legacy names). */
function reportStages(report) {
  if (!report || typeof report !== "object") return 0;
  const raw =
    report.processedStages ??
    report.processedCount ??
    report.processedStagesCount;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function reportWorkedDays(report) {
  if (!report || typeof report !== "object") return 0;
  const n = Number(report.totalWorkedDays ?? report.totalWorkedHour ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function formatTaskCount(report) {
  return reportStages(report);
}

function formatStagesPerDays(report) {
  return `${reportStages(report)}/${reportWorkedDays(report)}`;
}

function doctorDisplayName(row) {
  const name = row?.fullName != null ? String(row.fullName).trim() : "";
  if (name) return name;
  const un = row?.userName != null ? String(row.userName).trim() : "";
  if (un) return un;
  const em = row?.email != null ? String(row.email).trim() : "";
  return em || "—";
}

export function parseDoctorStatsBody(
  body,
  pageLimit = DOCTOR_STATS_PAGE_LIMIT,
) {
  const list = Array.isArray(body?.ccUsersList) ? body.ccUsersList : [];
  const tp = Number(body?.totalPages);
  if (Number.isFinite(tp) && tp >= 1) {
    return { list, totalPages: tp };
  }
  const total = Number(body?.total);
  if (Number.isFinite(total) && total >= 0) {
    return {
      list,
      totalPages: Math.max(1, Math.ceil(total / pageLimit)),
    };
  }
  return { list, totalPages: list.length ? 1 : 1 };
}

function LoadingOverlay() {
  return (
    <div
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-white/90"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span
        className="inline-block w-10 h-10 border-[3px] border-[#017C71] border-t-transparent rounded-full animate-spin"
        aria-hidden
      />
      <span className="text-sm font-lufga text-gray-600">
        Loading statistics…
      </span>
    </div>
  );
}

/**
 * @param {string|null|undefined} [doctorId] — when set, passed as `doctorId` query param (same as chart/report filters)
 * @param {object|null} [prefetchedPage1Body] — response body from GET …/cc-doctor-case-count-report page=1
 * @param {boolean|undefined} [prefetchPage1Ready] — when defined, parent owns first-page fetch (prefetch on report load)
 */
export default function DutStatisticsView({
  doctorId = null,
  prefetchedPage1Body = null,
  prefetchPage1Ready = undefined,
  refreshNonce = 0,
}) {
  const parentIntegrated = prefetchPage1Ready !== undefined;

  const doctorIdParam =
    doctorId != null && String(doctorId).trim() !== ""
      ? String(doctorId).trim()
      : undefined;

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** Avoid re-fetch loop when parent prefetch finished with no body (fallback fetch once). */
  const page1FallbackDoneRef = useRef(false);

  const applyBody = useCallback((body) => {
    const { list, totalPages: tp } = parseDoctorStatsBody(
      body,
      DOCTOR_STATS_PAGE_LIMIT,
    );
    setRows(list);
    setTotalPages(tp);
  }, []);

  useEffect(() => {
    if (refreshNonce === 0) return;
    setPage(1);
    page1FallbackDoneRef.current = false;
  }, [refreshNonce]);

  const fetchPage = useCallback(
    async (pageNum) => {
      setLoading(true);
      setError(null);
      try {
        const res = await ReportServices.getDutCcUserCaseCountReport({
          limit: DOCTOR_STATS_PAGE_LIMIT,
          page: pageNum,
          ...(doctorIdParam ? { ccUserId: doctorIdParam } : {}),
        });
        applyBody(res?.data ?? {});
      } catch (e) {
        setRows([]);
        setError(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load doctor statistics.",
        );
      } finally {
        setLoading(false);
      }
    },
    [applyBody, doctorIdParam],
  );

  useEffect(() => {
    if (page !== 1) {
      page1FallbackDoneRef.current = false;
    }

    if (parentIntegrated && page === 1) {
      if (!prefetchPage1Ready) {
        setLoading(true);
        setError(null);
        return;
      }
      if (prefetchedPage1Body) {
        page1FallbackDoneRef.current = false;
        applyBody(prefetchedPage1Body);
        setError(null);
        setLoading(false);
        return;
      }
      if (!page1FallbackDoneRef.current) {
        page1FallbackDoneRef.current = true;
        fetchPage(1);
      }
      return;
    }

    if (!parentIntegrated && page === 1) {
      fetchPage(1);
      return;
    }

    if (page > 1) {
      fetchPage(page);
    }
  }, [
    page,
    parentIntegrated,
    prefetchPage1Ready,
    prefetchedPage1Body,
    applyBody,
    fetchPage,
    doctorIdParam,
  ]);

  return (
    <div className="bg-white rounded-xl lg:rounded-[20px] overflow-hidden border border-gray-100 max-w-full">
      {error && (
        <div className="px-4 py-3 text-sm text-red-600 bg-red-50 border-b border-red-100 font-lufga">
          {error}
        </div>
      )}
      <div
        className="relative overflow-x-auto overflow-y-hidden max-w-full min-h-[min(360px,50vh)]"
        style={{ scrollbarGutter: "stable" }}
      >
        <table
          className="w-full min-w-[700px] border-collapse"
          aria-busy={loading}
        >
          <thead>
            <tr className="bg-white border-b border-gray-200">
              <th className={`py-2.5 lg:py-3 px-4 text-left ${HEADER_CLASS}`}>
                Doctor
              </th>
              <th className={`py-2.5 lg:py-3 px-4 text-right ${HEADER_CLASS}`}>
                Today Tasks
              </th>
              <th className={`py-2.5 lg:py-3 px-4 text-right ${HEADER_CLASS}`}>
                Yesterday Tasks
              </th>
              <th className={`py-2.5 lg:py-3 px-4 text-right ${HEADER_CLASS}`}>
                Weekly Tasks
              </th>
              <th className={`py-2.5 lg:py-3 px-4 text-right ${HEADER_CLASS}`}>
                Monthly Tasks
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr aria-hidden>
                <td colSpan={5} className="p-0 h-[min(280px,45vh)] border-0" />
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className={`py-10 px-4 text-center ${CELL_CLASS} text-gray-500`}
                >
                  No doctors found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                >
                  <td className={`py-3 lg:py-4 px-4 ${CELL_CLASS} text-left`}>
                    {doctorDisplayName(row)}
                  </td>
                  <td className={`py-3 lg:py-4 px-12 ${CELL_CLASS} text-right`}>
                    {formatTaskCount(row.todayReport)}
                  </td>
                  <td className={`py-3 lg:py-4 px-14 ${CELL_CLASS} text-right`}>
                    {formatTaskCount(row.yesterdayReport)}
                  </td>
                  <td
                    className={`py-3 lg:py-4 px-14 ${CELL_CLASS} text-right `}
                  >
                    {formatStagesPerDays(row.weeklyReport)}
                  </td>
                  <td className={`py-3 lg:py-4 px-14 ${CELL_CLASS} text-right`}>
                    {formatStagesPerDays(row.monthlyReport)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {loading && <LoadingOverlay />}
      </div>
      <div
        className={`flex justify-center p-4 border-t border-gray-100 ${
          loading ? "pointer-events-none opacity-50" : ""
        }`}
      >
        <HospitalCustomPaginaion
          page={page}
          totalPages={totalPages}
          setPage={setPage}
        />
      </div>
    </div>
  );
}
